/** Save confirmation for the notification editor: a humanized summary of the
 *  changed paths (chips) + an optional audit comment persisted on the
 *  Notification_Change row.
 */
import { Button, Chip, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import type { NotificationDeliveryMethod } from '@/api/notifications';
import { DELIVERY_METHOD_LABELS } from '@/api/notifications';
import { AppDialog } from '@/components/AppDialog';

import { channelSpec } from './delivery/channelFields';

const META_LABELS: Record<string, string> = {
  name: 'Name',
  enabled: 'Enabled',
  language_policy: 'Language policy',
  disable_default_blocks: 'Default blocks toggle',
  rules_match_type: 'Rules match mode',
  event: 'Event',
  roles: 'Triggered-by roles',
  apply_to_guests: 'Applies to guests',
  apply_to_observers: 'Applies to observers',
  booking_state: 'Booking state',
  tour_override_mode: 'Tour override mode',
  tours: 'Tours',
  ignore_user_nf_prefs: 'Ignore user preferences',
  lock_delivery: 'Delivery lock',
  lock_time: 'Delivery lock window',
  delivery_method_ordering: 'Method ordering',
  tags: 'Tags',
  atag: 'Access tags',
};

const DM_FIELD_LABELS: Record<string, string> = {
  enabled: 'Enabled',
  theme: 'Theme',
  sequence: 'Order',
};

function prettify(field: string): string {
  const text = field.replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function dmFieldLabel(method: number, field: string): string {
  const spec = channelSpec(method);
  const tpl = spec.templateFields.find((f) => f.field === field);
  if (tpl) return tpl.label;
  const known = DM_FIELD_LABELS[field];
  if (known) return known;
  if (field === spec.renderingTypeField) return 'Rendering type';
  if (field === spec.templateField) return 'Standard template';
  return prettify(field);
}

/** Translate one dirty path into a human label. */
export function humanizeChangedPath(
  path: string,
  deliveryMethods: NotificationDeliveryMethod[],
): string {
  if (path === 'block_refs') return 'Block references';
  if (path === 'rules') return 'Targeting rules';
  if (path === 'advanced_rules') return 'Advanced delivery rules';
  if (path === 'auto_replies') return 'Auto-reply (send another message)';
  if (path === 'delivery_methods') return 'Delivery method order';
  if (path === 'notification') return 'Notification settings';

  const dmMatch = /^delivery_methods\[(-?\d+)\](?:\.(.+))?$/.exec(path);
  if (dmMatch) {
    const dm = deliveryMethods.find((d) => d.id === Number(dmMatch[1]));
    const methodLabel = dm
      ? (DELIVERY_METHOD_LABELS[dm.method] ?? `Method ${dm.method}`)
      : `DM #${dmMatch[1]}`;
    if (!dmMatch[2]) return `${methodLabel} (all fields)`;
    return `${methodLabel} · ${dm ? dmFieldLabel(dm.method, dmMatch[2]) : prettify(dmMatch[2])}`;
  }

  if (path.startsWith('notification.')) {
    const key = path.slice('notification.'.length);
    return META_LABELS[key] ?? prettify(key);
  }
  return path;
}

interface Props {
  open: boolean;
  changedPaths: string[];
  deliveryMethods: NotificationDeliveryMethod[];
  defaultComment?: string;
  saving: boolean;
  onCancel: () => void;
  onSave: (comment: string) => void;
}

export function SaveCommentDialog({
  open,
  changedPaths,
  deliveryMethods,
  defaultComment,
  saving,
  onCancel,
  onSave,
}: Props) {
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (open) setComment(defaultComment ?? '');
  }, [open, defaultComment]);

  const labels = [...new Set(changedPaths.map((p) => humanizeChangedPath(p, deliveryMethods)))];

  return (
    <AppDialog
      open={open}
      onClose={onCancel}
      title="Save notification"
      size="sm"
      actions={
        <>
          <Button onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => onSave(comment.trim())} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <Stack spacing={2} sx={{ pt: 0.5 }}>
        <div>
          <Typography variant="caption" color="text.secondary">
            Changes
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            {labels.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No changes.
              </Typography>
            ) : (
              labels.map((label) => <Chip key={label} size="small" variant="outlined" label={label} />)
            )}
          </Stack>
        </div>
        <TextField
          label="Comment (optional)"
          multiline
          minRows={2}
          fullWidth
          size="small"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          helperText="Recorded on the change-history entry."
        />
      </Stack>
    </AppDialog>
  );
}
